import type { TimePickerTime } from 'customer-flows/components/ui-kit/types'
import type IntlService from 'ember-intl/services/intl'
import { groupTimesByLabel } from 'customer-flows/availability/times'
import { DateTime } from 'luxon'
import Logger from 'customer-flows/utils/logger'

export default function availability(args: CommandArgs) {
  const { stores, services } = args
  const availabilityStore: AvailabilityStore = stores['availability']
  const activityStore: ActivityStore = stores['activity']
  const bookingFlowStore: BookingFlowStore = stores['program/booking-flow']
  const intl: IntlService = services['intl']
  const eventTracker: EventTracker = services['event-tracker']

  /**
   * Returns a promise that resolves once the availability the engine is fetching is ready.
   * Reading the engine's promise is what schedules that fetch (stale-while-revalidate) and awaits
   * it — this does not fetch imperatively, it hands back a readiness handle callers `await` to
   * sequence loading state. No-ops (returns undefined) when there is no activity id to query.
   */
  function ensureAvailabilityDates() {
    if (!activityStore.id) {
      Logger.warn('No activity ID found in activity store. Cannot fetch availability dates')
      return
    }

    return availabilityStore.availabilityDates.promise()
  }

  /**
   * Records the outcome of a settled availability fetch: whether it failed, and otherwise whether
   * the scoped projection holds anything available
   */
  function trackAvailabilityOutcome() {
    const dates = availabilityStore.availabilityDates

    if (dates.error) {
      eventTracker.trackOnce('availability_error')
      return
    }

    const hasAvailability = (dates.value ?? []).some((date) => availabilityStore.isAvailableStatus(date.status))

    eventTracker.track(hasAvailability ? 'availability_available' : 'availability_empty')
  }

  /**
   * Re-runs step validation once the in-flight availability lands.
   *
   * `flowManager.allStepsValid` latches whatever the last validation returned, so a step that
   * answered before its data arrived needs this to correct it. No-ops when nothing is in flight,
   * which is what stops it re-arming itself.
   */
  async function revalidateStepsWhenSettled() {
    if (!availabilityStore.availabilityFetching) return

    await availabilityStore.availabilityDates.promise()

    const flowManager: FlowManager = services['flow-manager']
    await flowManager.evaluateSteps()
  }

  /**
   * hard-invalidates all availability the user has seen, e.g. after an order fails because tickets
   * are no longer available. Hard-invalidated entries stop being served everywhere and refetch as
   * a blocking first-load on the next read, so the tickets step shows loading (not stale data) on
   * re-entry and renders the corrected availability once it lands. Both dates AND times entries
   * are fetch-wired in the engine now, so `invalidate()`'s current-range force-refetch already
   * covers the selected-date times entry — no manual times refetch needed here.
   */
  function invalidateAvailability() {
    return availabilityStore.availabilityEngine.invalidate({ mode: 'hard' })
  }

  function clearStartTimeAndDuration() {
    availabilityStore.selectedTimeOverride = undefined
    availabilityStore.selectedAvailabilityDuration = undefined
  }

  function clearDuration() {
    availabilityStore.selectedAvailabilityDuration = undefined
  }

  function clearTime() {
    availabilityStore.selectedTimeOverride = undefined
  }

  /**
   * Sets the selected date. The engine fetches dates for the visible range, and (reactively, off
   * `selectedDate`) the selected-date times family — awaiting `availabilityDates.promise()` is what
   * drives both and resolves once they land. If the date still has no availability afterwards
   * (e.g. it fell outside the fetched range), the selection is cleared.
   * @param selectedDate
   */
  async function setSelectedDate(selectedDate?: string, { calendarStartDate }: { calendarStartDate?: string } = {}) {
    availabilityStore.selectedDate = selectedDate

    if (selectedDate) {
      eventTracker.trackOnce('Selected Calendar Date')
    }

    // seed the range center so the engine's fetch covers the target date.
    // The fetch scope is derived from the calendar's center — without this, the fetch uses the
    // current month and misses dates in other months (e.g. when called from preflight
    // before the date-picker has had a chance to set a center).
    if (calendarStartDate && !availabilityStore.hasCenter) {
      setCenter(DateTime.fromISO(calendarStartDate).toJSDate())
      availabilityStore.calendarStartDate = calendarStartDate
    }

    // no date given, we fetch dates and return
    if (!selectedDate) {
      await ensureAvailabilityDates()
      trackAvailabilityOutcome()
      return
    }

    // Dates only. The check below is "does this date have availability", which the dates families
    // answer; a bare `promise()` would also wait on the selected-date times fetch that setting
    // `selectedDate` above just triggered — and the date-picker awaits THIS before calling
    // `updateFlow`, so that wait delayed the whole next step behind a background verification.
    await availabilityStore.availabilityDates.promise('dates')

    trackAvailabilityOutcome()

    if (availabilityStore.selectedDate !== selectedDate) return

    const selectedDateAvailabilityDate = availabilityStore.availabilityDates.value?.find(
      date => date.date === selectedDate
    )

    // if still not found, we clear the selected date and return
    // as selected date doesn't have availability
    if (!selectedDateAvailabilityDate) {
      Logger.warn('Availability: Selected date has no availability after fetch, clearing selection', { selectedDate })
      availabilityStore.selectedDate = undefined

      if (bookingFlowStore.timeSelectionMode === 'HIDDEN') {
        clearTime()
      }
    }
  }

  /**
   * Converts availability times into a format suitable for the time picker component.
   */
  function toTimePickerTimes(availability: AvailabilityTime[]): TimePickerTime[] {
    const grouped = groupTimesByLabel(availability)

    return Object.entries(grouped).map(([label, times]) => {
      const available = times.some((time) => availabilityStore.isAvailableStatus(time.status))
      const bookable = times.some((time) => availabilityStore.isBookableStatus(time.status))
      const anytime = times.some((time) => time.duration?.length?.unit === 'FLEXIBLE')
      const isoTime = times[0]!.from

      return {
        isoTime,
        label,
        available,
        bookable,
        anytime
      }
    })
  }

  /**
   * Returns a details string to display alongside the given time string in the time picker,
   * or undefined if no details are available.
   *
   * @param time - The time string to get details for
   *
   * e.g. "Call to book"
   */
  function getDetailsForTime(time: string): string | undefined {
    const statuses = availabilityStore.getStatusesForTime(time)
    if (statuses.length > 0 && statuses.every((status) => status === 'CALL_TO_BOOK')) {
      return bookingFlowStore.callToBookText
    }

    if (bookingFlowStore?.minShowLeftCutoff) {
      const maxSpotsLeft = availabilityStore.getMaxSpotsLeftForTime(time)

      if (maxSpotsLeft > 0 && maxSpotsLeft <= bookingFlowStore.minShowLeftCutoff) {
        return intl.t('booking.time-picker.spots-left-cuttoff.message', { count: maxSpotsLeft })
      }

      if (maxSpotsLeft === 0) {
        return bookingFlowStore.overrideSoldOutText ?? intl.t('common.sold-out')
      }
    }

    if (statuses.length > 0 && statuses.every((status) => status === 'SOLD_OUT')) {
      return bookingFlowStore.overrideSoldOutText ?? intl.t('common.sold-out')
    }

    return undefined
  }

  /**
   * Returns the number of spots remaining for a time if it falls within the
   * spots-left cutoff range (0 < spots <= minShowLeftCutoff), null otherwise.
   *
   * Used alongside getDetailsForTime to decide whether spots-left info should
   * be surfaced as a corner flag (when pricing is also shown) or as detail text.
   */
  function getSpotsLeftForTime(time: string): number | null {
    if (!bookingFlowStore?.minShowLeftCutoff) return null

    const statuses = availabilityStore.getStatusesForTime(time)
    if (statuses.length > 0 && statuses.every((status) => status === 'CALL_TO_BOOK')) return null

    const maxSpotsLeft = availabilityStore.getMaxSpotsLeftForTime(time)
    if (maxSpotsLeft > 0 && maxSpotsLeft <= bookingFlowStore.minShowLeftCutoff) {
      return maxSpotsLeft
    }

    return null
  }

  /**
   * Move the calendar to a month directly — mount, or selecting a date outside the visible month.
   * Not a scrub, so display and fetch move together: the settled center takes the new month and any
   * in-flight scrub hop is dropped, since a direct move supersedes it. The single write path for
   * both centers outside the scrub pair below; writing `settledCenter` on its own would leave a
   * stale `pendingCenter` overriding the display.
   */
  function setCenter(center: Date) {
    availabilityStore.settledCenter = center
    availabilityStore.pendingCenter = undefined
  }

  // One hop of a month navigation. Unconditional: the visible grid follows `pendingCenter` and
  // moves immediately, while the fetch scope stays on the settled month, so a burst of hops fetches
  // nothing on the way past. Pair with the debounced `settleFetchScope` below.
  function navigateMonth(center: Date) {
    availabilityStore.pendingCenter = center
  }

  // Debounced tail (the date-picker injects this with { debounce, restartable }): navigation has
  // stopped, so promote the month it stopped on — the fetch scope snaps to the now-visible month
  // and fetches it. A no-op when nothing is pending, to avoid dirtying tracked state.
  function settleFetchScope() {
    if (!availabilityStore.pendingCenter) return
    setCenter(availabilityStore.pendingCenter)
  }

  // Promotes the live ticket quantities into the fetch scope — the quantities counterpart of
  // `settleFetchScope`, and the only writer of `settledQuantities`.
  function settleTicketQuantities() {
    availabilityStore.settledQuantities = availabilityStore.resourceOptionQuantities
  }

  return {
    settleTicketQuantities,
    ensureAvailabilityDates,
    revalidateStepsWhenSettled,
    invalidateAvailability,
    clearStartTimeAndDuration,
    clearDuration,
    clearTime,
    setSelectedDate,
    toTimePickerTimes,
    getDetailsForTime,
    getSpotsLeftForTime,
    setCenter,
    navigateMonth,
    settleFetchScope
  }
}

declare global {
  export type AvailabilityCommands = ReturnType<typeof availability>

  interface CommandRegistry {
    availability: AvailabilityCommands
  }
}
